// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { memo, useEffect, useMemo, useRef, useState } from "react";
import type { Activity } from "@/hooks/api/use-account-activities";
import { useWindowWidth } from "@/hooks/use-window-width";
import { formatDate } from "@/lib/date-formatting";

// Interface for the tooltip component props
interface ActivityDotTooltipProps {
  date: string;
  activities: Activity[];
  position: { x: number; y: number } | null;
}

// Calculate tooltip position based on dot position
const tooltipOffset = { x: 0, y: -10 }; // Show tooltip above the dot

// Tooltip component for activity dots
const ActivityDotTooltip = memo(
  ({ date, activities, position }: ActivityDotTooltipProps) => {
    // Create a ref to measure the tooltip dimensions
    const tooltipRef = useRef<HTMLDivElement>(null);

    // State to track measured dimensions
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // State to track final position after measurement
    const [finalPosition, setFinalPosition] = useState({ left: 0, top: 0 });

    // State to track if the tooltip has been measured
    const [isMeasured, setIsMeasured] = useState(false);

    // Current viewport width (reactive to resize)
    const viewportWidth = useWindowWidth();

    // Update position when dimensions or position change
    useEffect(() => {
      if (!position || !isMeasured) return;

      // Get actual measured dimensions
      const tooltipWidth = dimensions.width;
      const tooltipHeight = dimensions.height;

      // Get viewport dimensions
      const viewportHeight =
        typeof window !== "undefined" ? window.innerHeight : 800;

      // Default position (center above dot)
      let x = position.x - tooltipWidth / 2;
      let y = position.y - tooltipHeight + tooltipOffset.y;

      // Ensure margins from edges
      const margin = 20;

      // Check if tooltip would extend beyond right edge
      if (x + tooltipWidth > viewportWidth - margin) {
        x = Math.max(margin, viewportWidth - tooltipWidth - margin);
      }

      // Check if tooltip would extend beyond left edge
      if (x < margin) {
        x = margin;
      }

      // Check if tooltip would extend beyond top edge
      if (y < margin) {
        // Position below dot if it doesn't fit above
        y = position.y + 20;
      }

      // Check if tooltip would extend beyond bottom edge
      if (y + tooltipHeight > viewportHeight - margin) {
        // Position above dot if it doesn't fit below
        y = Math.max(margin, position.y - tooltipHeight - 10);
      }

      // Update position
      setFinalPosition({ left: x, top: y });
    }, [position, dimensions, isMeasured, viewportWidth]);

    // Measure the tooltip after it renders
    useEffect(() => {
      if (tooltipRef.current && !isMeasured) {
        const { width, height } = tooltipRef.current.getBoundingClientRect();
        setDimensions({ width, height });
        setIsMeasured(true);
      }
    }, [isMeasured]);

    if (!position) return null;

    return (
      <div
        ref={tooltipRef}
        className="fixed bg-popover text-popover-foreground rounded shadow-lg text-xs z-[1000] p-2 animate-in fade-in zoom-in-95 duration-150 border border-border whitespace-normal"
        style={{
          maxWidth: "600px", // Increase maximum width to allow more content
          minWidth: "100px", // Ensure minimum width
          width: "auto", // Allow content to determine width
          pointerEvents: "none", // Ensure tooltip doesn't interfere with mouse events

          // Transition for smoother position updates (only after measurement)
          transition: isMeasured ? "left 0.1s, top 0.1s" : "none",

          // Use measured or initial position
          ...(isMeasured
            ? { left: `${finalPosition.left}px`, top: `${finalPosition.top}px` }
            : {
                opacity: 0, // Hide until measured
                left: `${position.x - 150}px`, // Roughly center initially
                top: `${position.y - 50}px`,
              }),
        }}
      >
        <div className="font-medium mb-1">
          {activities.length}{" "}
          {activities.length === 1 ? "touchpoint" : "touchpoints"} matching
          filters on {formatDate(date)}
        </div>
        <div className="space-y-1 max-h-[200px] overflow-y-auto pl-2">
          {activities.map((activity: Activity, idx: number) => {
            // Determine if this is an engagement or bid
            const isEngagement = activity.direction === "IN";
            const isBid = activity.direction === "OUT";

            return (
              <div key={idx} className="text-xs flex items-start gap-1.5">
                {/* Show colored dot to indicate type */}
                {activity.direction && (
                  <div
                    className={`h-1.5 w-1.5 rounded-full flex-shrink-0 mt-1 ${
                      isEngagement
                        ? "bg-primary" // Blue for engagements
                        : "bg-muted-foreground/60" // Grey for bids
                    }`}
                  />
                )}
                <span
                  className={
                    activity.direction
                      ? "text-muted-foreground break-normal"
                      : "text-muted-foreground pl-3 break-normal"
                  }
                >
                  {activity.activity}
                  {activity.direction && (
                    <span className="ml-1 text-[10px] text-muted-foreground/50">
                      ({isEngagement ? "engagement" : isBid ? "bid" : "unknown"}
                      )
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

ActivityDotTooltip.displayName = "ActivityDotTooltip";

export interface FilteredActivityDotsProps {
  data: Activity[];
  domain: readonly [number, number];
  height: number;
  onDateClick?: (date: string) => void;
}

const FilteredActivityDots = memo(
  ({ data, domain, onDateClick }: FilteredActivityDotsProps) => {
    // Group activities by date to show a single consolidated dot per day
    const groupedActivities = useMemo(() => {
      // First group all activities by their date, normalizing to YYYY-MM-DD format
      const dateGroups = new Map<string, Activity[]>();

      data.forEach((activity) => {
        // Ensure consistent date format by normalizing to YYYY-MM-DD
        // This prevents issues if dates have different time components
        const fullDate = new Date(activity.date);
        const normalizedDate = fullDate.toISOString().split("T")[0]; // Extract YYYY-MM-DD part

        const existing = dateGroups.get(normalizedDate) || [];
        dateGroups.set(normalizedDate, [...existing, activity]);
      });

      return dateGroups;
    }, [data]);

    // Determine if each day has any engagement (IN) activities
    // We'll always prioritize showing the blue engagement dot if there are any engagements
    const groupDirections = useMemo(() => {
      const directions = new Map<string, string>();
      groupedActivities.forEach((activities, date) => {
        // Check if there are any IN activities
        const hasEngagements = activities.some(
          (activity) => activity.direction === "IN",
        );

        // If there are any engagement activities, show the blue dot
        // Otherwise, show the grey bid dot
        directions.set(date, hasEngagements ? "IN" : "OUT");
      });
      return directions;
    }, [groupedActivities]);

    // State to track which dot is being hovered and its position
    const [hoverInfo, setHoverInfo] = useState<{
      date: string;
      activities: Activity[];
      position: { x: number; y: number } | null;
    } | null>(null);

    // Track tooltip visibility with a slight delay to prevent flickering
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const hoverTimer = useRef<number | null>(null);

    // Clear timer on unmount
    useEffect(() => {
      return () => {
        if (hoverTimer.current) {
          window.clearTimeout(hoverTimer.current);
        }
      };
    }, []);

    // Function to handle mouse enter on dot
    const handleMouseEnter = (
      date: string,
      activities: Activity[],
      event: React.MouseEvent,
    ) => {
      // Get dot position in viewport coordinates for fixed positioning
      const rect = event.currentTarget.getBoundingClientRect();
      const position = {
        x: rect.left + rect.width / 2, // Center of the dot
        y: rect.top, // Top of the dot
      };

      // Set hover info immediately
      setHoverInfo({
        date,
        activities,
        position,
      });

      // Show tooltip after a brief delay to prevent flickering on rapid movements
      if (hoverTimer.current) {
        window.clearTimeout(hoverTimer.current);
      }

      hoverTimer.current = window.setTimeout(() => {
        setTooltipVisible(true);
      }, 50);
    };

    // Function to handle mouse leave on dot
    const handleMouseLeave = () => {
      if (hoverTimer.current) {
        window.clearTimeout(hoverTimer.current);
      }

      // Hide tooltip after a delay to prevent flickering when moving between dots
      hoverTimer.current = window.setTimeout(() => {
        setTooltipVisible(false);
        setHoverInfo(null);
      }, 100);
    };

    return (
      <>
        {/* Render the tooltip if we have hover info */}
        {tooltipVisible && hoverInfo && (
          <ActivityDotTooltip
            date={hoverInfo.date}
            activities={hoverInfo.activities}
            position={hoverInfo.position}
          />
        )}

        {/* Render the dots */}
        <div className="relative w-full h-full">
          {Array.from(groupedActivities.entries()).map(
            ([date, activities]: [string, Activity[]]) => {
              const timestamp = new Date(date).getTime();
              if (timestamp < domain[0] || timestamp > domain[1]) return null;

              // Calculate x position based on domain
              const x =
                ((timestamp - domain[0]) / (domain[1] - domain[0])) * 100;

              return (
                <div
                  key={date}
                  className={`absolute rounded-full transition-all cursor-pointer ${
                    groupDirections.get(date) === "IN"
                      ? "bg-primary" // Blue for engagements (IN)
                      : "bg-muted-foreground/60" // Grey for bids (OUT)
                  }`}
                  style={{
                    left: `${x}%`,
                    bottom: "0px",
                    zIndex: 50,
                    // Make engagement dots twice the size of bid dots
                    width: groupDirections.get(date) === "IN" ? "6px" : "4px",
                    height: groupDirections.get(date) === "IN" ? "6px" : "4px",
                    // Center the dot properly
                    transform: "translate(-50%, 50%)",
                    // Hover size - keep the same hover growth proportion for both
                    transition: "width 0.15s, height 0.15s",
                  }}
                  onMouseOver={(e) => {
                    // Increase size on hover
                    e.currentTarget.style.width =
                      groupDirections.get(date) === "IN" ? "9px" : "6px";
                    e.currentTarget.style.height =
                      groupDirections.get(date) === "IN" ? "9px" : "6px";
                  }}
                  onMouseOut={(e) => {
                    // Return to original size
                    e.currentTarget.style.width =
                      groupDirections.get(date) === "IN" ? "6px" : "4px";
                    e.currentTarget.style.height =
                      groupDirections.get(date) === "IN" ? "6px" : "4px";
                  }}
                  onClick={() => {
                    // Get the first activity's original date string to maintain compatibility with the onDateClick handler
                    const firstActivity = activities[0];
                    if (firstActivity) {
                      onDateClick?.(firstActivity.date);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (activities.length > 0) {
                      handleMouseEnter(activities[0].date, activities, e);
                    }
                  }}
                  onMouseLeave={handleMouseLeave}
                />
              );
            },
          )}
        </div>
      </>
    );
  },
);

FilteredActivityDots.displayName = "FilteredActivityDots";

export default FilteredActivityDots;
